import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Phone, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { apiErrorMessage } from '../lib/apiError';

const Auth = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name || !formData.email || !formData.password) {
          toast.error('Please fill in all required fields');
          setLoading(false);
          return;
        }
        if (formData.password.length < 8) {
          toast.error('Password must be at least 8 characters');
          setLoading(false);
          return;
        }
        await register(formData.name, formData.email, formData.password, formData.phone);
        toast.success('Account created successfully!');
      }
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Authentication failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center overflow-hidden" data-testid="auth-page">
      {/* Background */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('/images/resort-hut-night.jpg')` }}
      />
      <div className="absolute inset-0 bg-void/70" />

      {/* Decorative gold line top */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />

      {/* Card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className="relative z-10 w-full max-w-sm mx-4"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <p className="overline text-gold-500 tracking-widest text-xs mb-1">Rudragarh Resort</p>
          <h1 className="font-heading text-3xl text-white">
            {isLogin ? 'Welcome Back' : 'Join Us'}
          </h1>
          <p className="text-foreground/50 text-sm mt-1">
            {isLogin ? 'Sign in to manage your bookings' : 'Create an account to start your journey'}
          </p>
        </div>

        {/* Card body */}
        <div className="bg-void/80 backdrop-blur-md border border-gold-500/20">
          {/* Toggle */}
          <div className="flex border-b border-gold-500/20">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-3 text-xs uppercase tracking-widest transition-all ${
                isLogin ? 'bg-gold-500 text-black' : 'text-foreground/50 hover:text-gold-500'
              }`}
              data-testid="auth-login-tab"
            >
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-3 text-xs uppercase tracking-widest transition-all ${
                !isLogin ? 'bg-gold-500 text-black' : 'text-foreground/50 hover:text-gold-500'
              }`}
              data-testid="auth-register-tab"
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-5">
            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  key="name"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">Full Name *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full input-default pl-11 pr-4 text-sm"
                      placeholder="Your full name"
                      data-testid="auth-name"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div>
              <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">Email *</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full input-default pl-11 pr-4 text-sm"
                  placeholder="you@example.com"
                  autoComplete="email"
                  data-testid="auth-email"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">Password *</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full input-default pl-11 pr-11 text-sm"
                  placeholder="••••••••"
                  minLength={8}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  data-testid="auth-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-gold-500 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <AnimatePresence mode="wait">
              {!isLogin && (
                <motion.div
                  key="phone"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <label className="text-xs text-foreground/50 uppercase tracking-widest mb-2 block">Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gold-500/70" size={16} />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9+\s-]/g, '');
                        setFormData({ ...formData, phone: val });
                      }}
                      className="w-full input-default pl-11 pr-4 text-sm"
                      placeholder="+91 98765 43210"
                      maxLength={15}
                      data-testid="auth-phone"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="auth-submit"
            >
              {loading ? 'Please wait...' : isLogin ? 'Sign In' : 'Create Account'}
            </button>

            <p className="text-center text-foreground/40 text-xs">
              {isLogin ? "Don't have an account? " : 'Already have an account? '}
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-gold-500 hover:underline"
              >
                {isLogin ? 'Register' : 'Login'}
              </button>
            </p>
          </form>
        </div>

        {/* Footer link */}
        <p className="text-center mt-6">
          <a
            href="/"
            className="text-xs text-foreground/40 hover:text-gold-500 transition-colors tracking-widest uppercase"
          >
            ← Return to Website
          </a>
        </p>
      </motion.div>

      {/* Decorative gold line bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold-500/60 to-transparent" />
    </div>
  );
};

export default Auth;
